'use client';

import { useFunnelAttribution, useFunnelStart } from '@/components/funnel-tracking';
import { Field, Form, Formik, type FormikHelpers } from 'formik';
import { boolean, object, string } from 'yup';
import {
  InteractionApiError,
  isTerminalInteractionError,
  reportInteractionFailure,
  signupInteraction,
} from '@/app/utils/auth-interactions-api';
import type { InteractionApprovedHandler, InteractionTerminalHandler } from './types';
import {
  FormIntro,
  FormSwitchButton,
  InputFieldset,
  TurnstileField,
  formErrorMessage,
  turnstileSiteKey,
  turnstileTheme,
  useTurnstileReset,
} from './shared-fields';

interface SignupFormValues {
  base: string;
  name: string;
  email: string;
  password: string;
  newsletter: boolean;
  turnstile: string;
}

// Keep in sync with the legacy signup schema in app/utils/auth-api.ts.
const signupValidationSchema = object().shape({
  name: string().min(2, 'Too short').max(100, 'Too long').required('Required'),
  email: string().email('Invalid email').required('Required'),
  password: string().min(10, 'Must be at least 10 characters').required('Required'),
  newsletter: boolean().default(true),
  turnstile: string().required('Please complete the CAPTCHA'),
});

export default function InteractionSignupForm({
  interactionToken,
  onApproved,
  onTerminal,
  onSwitchToLogin,
  hideHeader = false,
  allowOutboundLinks = true,
}: {
  interactionToken: string;
  onApproved: InteractionApprovedHandler;
  onTerminal: InteractionTerminalHandler;
  onSwitchToLogin: () => void;
  hideHeader?: boolean;
  allowOutboundLinks?: boolean;
}) {
  useFunnelStart('Signup Begin');
  const getAttribution = useFunnelAttribution();
  const { turnstileKey, resetTurnstile } = useTurnstileReset();

  function resetSubmittedTurnstile(actions: FormikHelpers<SignupFormValues>) {
    actions.setFieldValue('turnstile', '', false);
    resetTurnstile();
  }

  return (
    <>
      {!hideHeader && (
        <FormIntro title="Create account">
          <p>Enter your information below to sign up for a free Penzu account.</p>
          <p>
            Already have an account?{' '}
            <FormSwitchButton onClick={onSwitchToLogin}>Log in here.</FormSwitchButton>
          </p>
        </FormIntro>
      )}

      <Formik
        initialValues={{
          base: '',
          name: '',
          email: '',
          password: '',
          newsletter: true,
          turnstile: '',
        }}
        validationSchema={signupValidationSchema}
        onSubmit={async (values, actions: FormikHelpers<SignupFormValues>) => {
          try {
            const result = await signupInteraction({
              interactionToken,
              attribution: getAttribution(),
              email: values.email,
              password: values.password,
              fullName: values.name,
              receiveMarketingEmails: values.newsletter,
              turnstile: values.turnstile,
            });
            const completed = await onApproved(result.resumeUrl);
            if (!completed) {
              actions.setSubmitting(false);
            }
          } catch (error) {
            actions.setSubmitting(false);
            resetSubmittedTurnstile(actions);
            if (error instanceof InteractionApiError) {
              if (isTerminalInteractionError(error)) {
                onTerminal(error);
                return;
              }
              actions.setErrors({ base: formErrorMessage(error) });
              return;
            }
            reportInteractionFailure(error, {
              source: 'interaction-signup-form',
              endpoint: '/api/auth/interactions/signup',
              operation: 'submit',
            });
            actions.setErrors({
              base: 'Signup failed: Please try again.',
            });
          }
        }}
      >
        {({ isSubmitting, isValid, dirty, errors }) => (
          <Form>
            {errors.base && <div className="form-error">{errors.base}</div>}
            <fieldset className="max-w-sm md:max-w-full md:w-lg flex flex-col space-y-4 mb-4">
              <InputFieldset
                name="name"
                label="Full name"
                type="text"
                placeholder="eg. Jessica Smith"
                autoComplete="name"
              />
              <InputFieldset
                name="email"
                label="Email"
                type="email"
                placeholder="eg. you@example.com"
                autoComplete="username"
              />
              <InputFieldset
                name="password"
                label="Create a password"
                type="password"
                placeholder="Create a strong password"
                autoComplete="new-password"
              />
            </fieldset>
            <fieldset className="max-w-sm md:w-[32rem] flex flex-col space-y-4 mb-6">
              <div>
                <Field id="newsletter" name="newsletter" type="checkbox" />
                <label htmlFor="newsletter" className="ml-2 text-sm text-slate-600">
                  Send me occasional updates from Penzu
                </label>
              </div>
              <Field
                name="turnstile"
                component={TurnstileField}
                siteKey={turnstileSiteKey}
                theme={turnstileTheme}
                widgetKey={turnstileKey}
                action="signup"
                onWidgetError={(error: unknown) =>
                  reportInteractionFailure(error, {
                    source: 'interaction-signup-form',
                    endpoint: 'turnstile',
                    operation: 'widget',
                  })
                }
              />
            </fieldset>

            <div>
              <button
                type="submit"
                className="btn text-white bg-blue-500 hover:bg-blue-700 w-full"
                disabled={isSubmitting || !isValid || !dirty}
              >
                Create account
              </button>
            </div>
            <div className="mt-4 text-sm text-slate-600">
              {allowOutboundLinks ? (
                <>
                  By signing up you agree to our{' '}
                  <a
                    href="/terms"
                    target="_blank"
                    className="underline text-slate-800 hover:text-slate-600"
                  >
                    Terms of Use
                  </a>{' '}
                  and{' '}
                  <a
                    href="/privacy"
                    target="_blank"
                    className="underline text-slate-800 hover:text-slate-600"
                  >
                    Privacy Policy
                  </a>
                  .
                </>
              ) : (
                <>By signing up you agree to Penzu&apos;s terms and privacy policy.</>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}
